import message from './message';

class MessageQueue {
    constructor() {
        this.queue = [];
        this.activeMessages = new Set(); // Track currently active messages by unique key
        this.isProcessing = false;
    }

    /**
     * Add a message to the queue if it's not already active or queued
     * @param {string} type - Message type: 'success', 'error', 'warning', 'info'
     * @param {string} uniqueKey - Unique identifier for the message (e.g., error code or message content)
     * @param {string} content - Message content
     * @param {number} duration - Duration in seconds
     * @param {Function} onClose - Callback when message closes
     */
    enqueue(type, uniqueKey, content, duration = 3, onClose = null) {
        // Check if message with this key is already active or queued
        if (this.activeMessages.has(uniqueKey)) {
            console.log(`Message with key "${uniqueKey}" is already active or queued. Skipping.`);
            return;
        }

        // Add to active set and queue
        this.activeMessages.add(uniqueKey);
        this.queue.push({ type, uniqueKey, content, duration, onClose });

        // Process queue if not already processing
        if (!this.isProcessing) {
            this.processQueue();
        }
    }

    /**
     * Process the queue one message at a time
     */
    async processQueue() {
        if (this.queue.length === 0) {
            this.isProcessing = false;
            return;
        }

        this.isProcessing = true;
        const messageItem = this.queue.shift();

        // Show message based on type
        const messageMethod = message[messageItem.type] || message.info;
        messageMethod(
            messageItem.content,
            messageItem.duration,
            () => {
                // Remove from active set when message closes
                this.activeMessages.delete(messageItem.uniqueKey);

                // Call original onClose callback if provided
                if (messageItem.onClose) {
                    messageItem.onClose();
                }

                // Process next message in queue
                this.processQueue();
            }
        );
    }

    /**
     * Clear all messages from queue
     */
    clear() {
        this.queue = [];
        this.activeMessages.clear();
        this.isProcessing = false;
        message.destroy();
    }

    /**
     * Check if a specific key is already in queue or active
     * @param {string} uniqueKey 
     * @returns {boolean}
     */
    has(uniqueKey) {
        return this.activeMessages.has(uniqueKey);
    }

    // Convenience methods for different message types
    success(uniqueKey, content, duration = 3, onClose = null) {
        this.enqueue('success', uniqueKey, content, duration, onClose);
    }

    error(uniqueKey, content, duration = 3, onClose = null) {
        this.enqueue('error', uniqueKey, content, duration, onClose);
    }

    warning(uniqueKey, content, duration = 3, onClose = null) {
        this.enqueue('warning', uniqueKey, content, duration, onClose);
    }

    info(uniqueKey, content, duration = 3, onClose = null) {
        this.enqueue('info', uniqueKey, content, duration, onClose);
    }
}

// Export singleton instance
const messageQueue = new MessageQueue();
export default messageQueue;
